import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

export default function Login() {
  const capturedEmail = useAppStore((s) => s.capturedEmail);
  const sessionId = useAppStore((s) => s.sessionId);
  const [identifier, setIdentifier] = useState(capturedEmail ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loginUser, completeOnboarding, startOnboarding } = useAppStore();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: identifier, password, anonymousSessionId: sessionId }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Login failed");
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
        subscriptionStatus: data.user.subscriptionStatus ?? "none",
        subscriptionPlan: data.user.subscriptionPlan ?? null,
      });
      completeOnboarding();
      toast({ title: "Welcome back!", description: `Logged in as ${data.user.email}` });
      setLocation("/");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewUser = () => {
    startOnboarding();
    setLocation("/");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground relative overflow-hidden px-4">
      <div className="bg-orb-1" aria-hidden="true" />
      <div className="bg-orb-2" aria-hidden="true" />
      <div className="bg-orb-3" aria-hidden="true" />
      <div className="bg-grid" aria-hidden="true" />
      <div className="bg-aurora" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl shadow-primary/10 p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <ScribPostLogo size="lg" />
            </div>
            <h1 className="font-display font-bold text-2xl">Welcome back</h1>
            <p className="text-sm text-muted-foreground">
              Sign in to pick up right where you left off.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
              >
                {error}
              </motion.div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="login-identifier">
                Email or username
              </label>
              <Input
                id="login-identifier"
                type="text"
                placeholder="you@email.com or username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="h-12 rounded-xl bg-background border-border"
                autoComplete="username"
                autoFocus
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="login-password">
                Password
              </label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-background border-border pr-12"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full h-12 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
              disabled={loading || !identifier || !password}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Log in
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <Link href="/forgot-password">
              <button
                type="button"
                className="w-full text-right text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Forgot your password?
              </button>
            </Link>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground">or</span>
            </div>
          </div>

          <button
            onClick={handleNewUser}
            className="w-full text-center text-sm text-primary hover:text-primary/80 font-medium transition-colors py-2"
          >
            New to ScribPost? Get started free →
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
          <Link href="/privacy">
            <span className="hover:text-foreground transition-colors cursor-pointer">Privacy Policy</span>
          </Link>
          <span>&middot;</span>
          <Link href="/terms">
            <span className="hover:text-foreground transition-colors cursor-pointer">Terms of Service</span>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
